"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"

type CurrentUser = { email: string; nickname: string | null; isAdmin: boolean }

export function avatarInitials(user: Pick<CurrentUser, "email" | "nickname">): string {
  const source = (user.nickname || user.email).trim()
  const words = source.split(/[\s@._-]+/).filter(Boolean)
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : source.slice(0, 2)).toUpperCase()
}

function avatarColor(email: string): string {
  const colors = ["#2563EB", "#7C3AED", "#DB2777", "#0891B2", "#059669"]
  const value = [...email].reduce((total, character) => total + character.charCodeAt(0), 0)
  return colors[value % colors.length]
}

export function UserMenu() {
  const router = useRouter()
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [open, setOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  useEffect(() => {
    fetch("/api/auth/me").then(async (response) => {
      if (response.ok) setUser(await response.json() as CurrentUser)
    }).catch(() => {})
  }, [])

  const logout = async () => {
    setLoggingOut(true)
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" })
      if (response.ok) {
        router.replace("/login")
        router.refresh()
      }
    } finally {
      setLoggingOut(false)
    }
  }

  if (!user) return <div className="h-9 w-9 rounded-full bg-[#E2E8F0]" aria-hidden="true" />

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="用户菜单"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="h-9 w-9 rounded-full overflow-hidden cursor-pointer ring-offset-2 focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
      >
        <svg viewBox="0 0 40 40" className="h-full w-full" aria-hidden="true">
          <circle cx="20" cy="20" r="20" fill={avatarColor(user.email)} />
          <text x="20" y="25" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">{avatarInitials(user)}</text>
        </svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-11 z-50 w-60 rounded-xl border border-[#E2E8F0] bg-white p-2 shadow-lg">
          <div className="border-b border-[#E2E8F0] px-3 py-2">
            <p className="truncate text-sm font-medium text-[#1E293B]">{user.nickname || user.email}</p>
            <p className="truncate text-xs text-[#64748B]">{user.email}</p>
          </div>
          {user.isAdmin && (
            <Link href="/admin" role="menuitem" onClick={() => setOpen(false)} className="mt-1 block rounded-lg px-3 py-2 text-sm text-[#334155] hover:bg-[#F1F5F9]">
              管理后台
            </Link>
          )}
          <button type="button" role="menuitem" onClick={logout} disabled={loggingOut} className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2] disabled:opacity-50 cursor-pointer">
            {loggingOut ? "正在退出…" : "退出登录"}
          </button>
        </div>
      )}
    </div>
  )
}
